import { ActionCaller, ActionHandle } from "arcjs/action/base";

import { LOAD_MAP_ACTION_NAME } from "lib/protocols/mapLoad";

export type LoadMapActionGoal = {
    map_id: string;
    variant_bmp_rel_path?: string;
    map_file_id?: string;
};

export type LoadMapActionFeedback = {
    stage?: string;
    progress?: number;
    status_message?: string;
    message?: string;
};

export type LoadMapPerformResult = {
    success: boolean;
    message?: string;
    error?: string;
    cancelled?: boolean;
};

export type LoadMapActionHandle = {
    promise: Promise<LoadMapPerformResult>;
    cancel: () => void;
};

/** Thrown by Map Manager when the user cancels an in-flight `/load_map` goal. */
export class MapLoadCancelledError extends Error {
    constructor() {
        super("Map load cancelled");
        this.name = "MapLoadCancelledError";
    }
}

interface LoadMapActionResult {
    success?: boolean;
    message?: string;
}

function isCancelledActionError(error: unknown): boolean {
    const errorStr = String(error);
    const lowerError = errorStr.toLowerCase();
    return (
        lowerError.includes("canceled") ||
        lowerError.includes("cancelled") ||
        lowerError.includes("status_canceled")
    );
}

/** Run `/load_map` (mission executor pipeline) and resolve when the goal completes. */
export function performLoadMapAction(
    actionCaller: ActionCaller | null,
    goal: LoadMapActionGoal,
    onFeedback?: (feedback: LoadMapActionFeedback) => void,
): LoadMapActionHandle {
    const mapId = goal.map_id?.trim();
    if (!mapId) {
        return {
            promise: Promise.resolve({
                success: false,
                error: "map_id is required",
            }),
            cancel: () => {},
        };
    }

    if (!actionCaller) {
        return {
            promise: Promise.resolve({
                success: false,
                error: "Action control source unavailable",
            }),
            cancel: () => {},
        };
    }

    const rosGoal: Record<string, string> = { map_id: mapId };
    const variant = goal.variant_bmp_rel_path?.trim();
    const mapFileId = goal.map_file_id?.trim();
    if (variant) {
        rosGoal.variant_bmp_rel_path = variant;
    }
    if (mapFileId) {
        rosGoal.map_file_id = mapFileId;
    }

    // The handle resolves asynchronously; a cancel() arriving before then is queued
    // and applied once the handle is available.
    let handle: ActionHandle | null = null;
    let cancelRequested = false;

    const promise = new Promise<LoadMapPerformResult>((resolve) => {
        actionCaller
            .sendGoal<Record<string, string>, LoadMapActionFeedback, LoadMapActionResult>(
                LOAD_MAP_ACTION_NAME,
                rosGoal,
                {
                    onResult: (result) => {
                        const payload = result as LoadMapActionResult;
                        resolve({
                            success: payload.success !== false,
                            message: payload.message,
                        });
                    },
                    onFeedback: (feedback) => {
                        if (!onFeedback) return;
                        const parsed = feedback as LoadMapActionFeedback & {
                            feedback?: LoadMapActionFeedback;
                        };
                        const nested = parsed.feedback;
                        onFeedback({
                            stage: parsed.stage ?? nested?.stage,
                            progress: parsed.progress ?? nested?.progress,
                            status_message:
                                parsed.status_message ??
                                nested?.status_message ??
                                parsed.message ??
                                nested?.message,
                        });
                    },
                    onFailed: (error) => {
                        resolve({
                            success: false,
                            error: String(error),
                            cancelled: isCancelledActionError(error),
                        });
                    },
                },
            )
            .then((h) => {
                handle = h;
                if (cancelRequested) void h.cancel();
            })
            .catch((error) => {
                resolve({
                    success: false,
                    error: String(error),
                    cancelled: isCancelledActionError(error),
                });
            });
    });

    return {
        promise,
        cancel: () => {
            cancelRequested = true;
            void handle?.cancel();
        },
    };
}
